import { useMemo, useState } from "react";
import { TabPanel, TabView } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row as TableRow } from "primereact/row";
import { Button } from "primereact/button";
import { formatCurrency } from "medere-ui-framework";
import { BudgetDTO } from "../../../dto/budget/BudgetDTO";
import { BudgetDetailDTO } from "../../../dto/budget/BudgetDetailDTO";
import {
    BILL_ITEM_CATEGORIES,
    BILL_ITEM_CATEGORY_CONFIG,
    BillItemCategory,
    getCategorySubtotal,
    getDuplicatedHealthPatientServices,
    getItemWarnings,
    getUnitsLabel,
    sortByHealthPatientService,
} from "./billItems";
import ItemDetailDrawer from "./ItemDetailDrawer";
import { WarningTags, formatApplicationDate, formatCoverage } from "./tableParts";

type Row = { rowKey: string; item: BudgetDetailDTO; index: number };

/** Categorías con avisos (servicio duplicado, distribución) en la columna Estado. */
const WITH_STATUS: BillItemCategory[] = ['professionalActivities', 'hospitalizationDays'];

/** Una pestaña de la vista previa: la tabla de la categoría como datos, sin fila de alta ni campos. */
function ReadOnlyTable({ category, items, onView }: { category: BillItemCategory; items: BudgetDetailDTO[]; onView: (index: number) => void }) {
    const config = BILL_ITEM_CATEGORY_CONFIG[category];
    const withStatus = WITH_STATUS.includes(category);
    const duplicated = useMemo(() => getDuplicatedHealthPatientServices(items), [items]);
    const rows: Row[] = useMemo(() => {
        const ordered = withStatus ? sortByHealthPatientService(items) : items.map((item, index) => ({ item, index }));
        return ordered.map(({ item, index }) => ({ rowKey: `${index}-${item.description}`, item, index }));
    }, [items, withStatus]);

    const footer = (
        <ColumnGroup>
            <TableRow>
                <Column footer="Subtotal" />
                <Column footer={null} />
                <Column footer={null} />
                <Column footer={null} />
                <Column footer={null} />
                <Column footer={formatCurrency(getCategorySubtotal(items).toNumber())} align="right" className="nv-col-money" />
                {withStatus && <Column footer={null} />}
                <Column footer={null} />
            </TableRow>
        </ColumnGroup>
    );

    return (
        <DataTable value={rows} dataKey="rowKey" scrollable scrollHeight="580px" footerColumnGroup={footer} emptyMessage={config.emptyMessage} className="nv-load-table">
            <Column header={config.itemHeader} className="nv-col-item" body={({ item }: Row) => <span className="nv-truncate nv-tip" data-pr-tooltip={item.description}>{item.description}</span>} />
            <Column header="Fecha" align="center" alignHeader="center" className="nv-col-date" body={({ item }: Row) => formatApplicationDate(item.applicationdate)} />
            <Column header={config.quantityHeader} align="center" alignHeader="center" className="nv-col-qty" body={({ item }: Row) => {
                const units = getUnitsLabel(item);
                return <span className="nv-tip" data-pr-tooltip={units.detail}>{units.text}</span>;
            }} />
            <Column header="Valor unitario" align="right" alignHeader="right" className="nv-col-money" body={({ item }: Row) => formatCurrency(item.unitprice ?? 0)} />
            <Column header="Cobertura OS" align="center" alignHeader="center" className="nv-col-pct" body={({ item }: Row) => formatCoverage(item.healthentitypercentagecoverage)} />
            <Column header="Total" align="right" alignHeader="right" className="nv-col-money" body={({ item }: Row) => <span className="nv-strong">{formatCurrency(item.valuemonth ?? 0)}</span>} />
            {withStatus && <Column header="Estado" className="nv-col-status" body={({ item }: Row) => <WarningTags warnings={getItemWarnings(category, item, duplicated)} />} />}
            <Column
                header={<span className="nv-sr-only">Acciones</span>}
                className="nv-col-actions"
                body={({ item, index }: Row) => (
                    <Button
                        type="button"
                        text
                        className="nv-icon nv-tip"
                        icon="pi pi-eye"
                        aria-label={`Ver detalle de ${item.description}`}
                        data-pr-tooltip="Ver detalle"
                        onClick={() => onView(index)}
                    />
                )}
            />
        </DataTable>
    );
}

/** Ítems de la vista previa: las mismas pestañas que el alta y la edición, en solo lectura. */
export default function BillItemsReadOnly({ budget }: { budget: BudgetDTO }) {
    const [activeIndex, setActiveIndex] = useState(0);
    const [viewing, setViewing] = useState<{ category: BillItemCategory; index: number } | null>(null);
    const items = (category: BillItemCategory) => (budget[category] ?? []) as BudgetDetailDTO[];
    const viewingItem = viewing ? items(viewing.category)[viewing.index] : undefined;

    return (
        <section className="nv-section" aria-labelledby="nv-items-title">
            <h2 className="nv-section-title" id="nv-items-title">Ítems</h2>
            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} className="nv-tabs">
                {BILL_ITEM_CATEGORIES.map(category => (
                    <TabPanel
                        key={category}
                        header={(
                            <span className="nv-tab-header">
                                {BILL_ITEM_CATEGORY_CONFIG[category].title}
                                <span className="nv-tab-count">{items(category).length}</span>
                            </span>
                        )}
                    >
                        <div className="nv-card">
                            <ReadOnlyTable category={category} items={items(category)} onView={(index) => setViewing({ category, index })} />
                        </div>
                    </TabPanel>
                ))}
            </TabView>
            {viewing && viewingItem && (
                <ItemDetailDrawer
                    key={`${viewing.category}-${viewing.index}`}
                    category={viewing.category}
                    item={viewingItem}
                    readOnly
                    onHide={() => setViewing(null)}
                />
            )}
        </section>
    );
}
